import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { listJobs, retryJob } from "../api";
import { BordeauxOceanLayer, HeroScrim } from "../components/HeroVisuals";
import { PageHelpHandle } from "../components/PageHelpHandle";
import { StatusChip, type StatusChipState } from "../components/StatusChip";
import { toast } from "../components/Toaster";
import type { JobRow } from "../types";
type RecordsSort = "urgency" | "balance" | "nextDate";
type RecordsStatusFilter = "all" | "open" | "outstanding" | "cleared" | "processing";

const TRIAGE_EXPLAINER =
  "Priority = case urgency from open items plus court proximity. It ranks follow-up work, not people.";
const LEDGER_PAGE_SIZE = 80;

function isInProgress(j: JobRow): boolean {
  return j.status === "queued" || j.status === "running" || j.status === "waiting_captcha";
}

function statusToken(j: JobRow): { key: Exclude<RecordsStatusFilter, "all">; chipState: StatusChipState; label: string; detail: string } {
  if (isInProgress(j)) {
    return { key: "processing", chipState: "running", label: "Processing", detail: "Capture still running" };
  }
  if (j.status === "error" || j.status === "canceled") {
    return { key: "open", chipState: "error", label: "Open", detail: "Capture did not complete" };
  }
  if ((j.havoc_score ?? 0) >= 65) {
    return { key: "outstanding", chipState: "stale", label: "Outstanding", detail: "Higher internal triage score" };
  }
  return { key: "cleared", chipState: "done", label: "Cleared", detail: "Captured and ready to review" };
}

function formatDate(s: string | null): string {
  if (!s) return "Not available";
  return new Date(s).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(s: string | null): string {
  if (!s) return "";
  return new Date(s).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

type LedgerView = "ledger" | "table";

const LEDGER_VIEW_KEY = "ledgerView";

export function RecordsPage() {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<RecordsSort>("urgency");
  const [statusFilter, setStatusFilter] = useState<RecordsStatusFilter>("all");
  const [filtering, setFiltering] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [view, setView] = useState<LedgerView>(() => {
    try {
      const v = localStorage.getItem(LEDGER_VIEW_KEY);
      return v === "table" ? "table" : "ledger";
    } catch {
      return "ledger";
    }
  });
  const [page, setPage] = useState(0);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const setViewPersisted = (v: LedgerView) => {
    setView(v);
    try { localStorage.setItem(LEDGER_VIEW_KEY, v); } catch {}
  };

  useEffect(() => {
    const handle = window.setTimeout(() => {
      searchInputRef.current?.focus({ preventScroll: true });
      searchInputRef.current?.select();
    }, 80);
    return () => window.clearTimeout(handle);
  }, []);

  const loadRecords = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    listJobs()
      .then((rows) => {
        setJobs(rows);
        setLastUpdated(new Date());
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const retryCapture = async (jobId: string) => {
    if (retryingId) return;
    setRetryingId(jobId);
    try {
      const updated = await retryJob(jobId);
      setJobs((prev) => prev.map((j) => (j.id === jobId ? updated : j)));
      toast("Capture queued for retry.", "success");
    } catch (err) {
      toast(err instanceof Error ? err.message : "Retry failed", "danger");
    } finally {
      setRetryingId(null);
    }
  };

  // Pre-filter list (no search query). We split this from the
  // post-filter list so we can show a "no results" empty state when
  // the query filtered everything out, distinct from the "no records
  // at all" state.
  const allCaptured = useMemo(
    () =>
      jobs.filter(
        (j) =>
          j.status === "error" ||
          j.status === "canceled" ||
          j.status === "completed" ||
          (j.record_count_captured !== null && (j.record_count_captured ?? 0) > 0),
      ),
    [jobs],
  );
  const captured = useMemo(() => {
    let xs = allCaptured;
    if (query.trim()) {
      const q = query.toLowerCase();
      xs = xs.filter(
        (j) => {
          let aliases: string[] = [];
          try {
            const parsed = JSON.parse(j.aliases || "[]");
            aliases = Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
          } catch {
            aliases = [];
          }
          return (
            `${j.first_name} ${j.last_name}`.toLowerCase().includes(q) ||
            j.date_of_birth.includes(q) ||
            aliases.some((a) => a.toLowerCase().includes(q))
          );
        },
      );
    }
    if (statusFilter !== "all") {
      xs = xs.filter((j) => statusToken(j).key === statusFilter);
    }
    return [...xs].sort((a, b) => {
      if (sort === "balance") {
        return (b.record_count_captured ?? 0) - (a.record_count_captured ?? 0) || b.created_at.localeCompare(a.created_at);
      }
      if (sort === "nextDate") {
        return (b.completed_at ?? b.started_at ?? b.created_at).localeCompare(a.completed_at ?? a.started_at ?? a.created_at);
      }
      const aToken = statusToken(a).key;
      const bToken = statusToken(b).key;
      const rank = (k: typeof aToken) => k === "open" ? 0 : k === "outstanding" ? 1 : k === "processing" ? 2 : 3;
      return rank(aToken) - rank(bToken)
        || (b.havoc_score ?? -1) - (a.havoc_score ?? -1)
        || b.created_at.localeCompare(a.created_at);
    });
  }, [allCaptured, query, sort, statusFilter]);

  useEffect(() => {
    setActiveIndex((i) => Math.min(Math.max(0, i), Math.max(0, captured.length - 1)));
  }, [captured.length]);
  useEffect(() => {
    setPage(0);
  }, [query, sort, statusFilter]);
  useEffect(() => {
    if (loading) return;
    setFiltering(true);
    const t = window.setTimeout(() => setFiltering(false), 120);
    return () => window.clearTimeout(t);
  }, [query, sort, statusFilter, loading]);

  const rosterDensity = Math.min(1, 0.36 + allCaptured.length / 120);
  const stale = lastUpdated ? Date.now() - lastUpdated.getTime() > 5 * 60_000 : false;
  const pageCount = Math.max(1, Math.ceil(captured.length / LEDGER_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * LEDGER_PAGE_SIZE;
  const visibleCaptured = captured.slice(pageStart, pageStart + LEDGER_PAGE_SIZE);
  const groupedLedgerRows = useMemo(() => {
    const groups = new Map<string, JobRow[]>();
    for (const row of visibleCaptured) {
      const token = statusToken(row);
      const key = sort === "urgency"
        ? token.label
        : (row.last_name.trim().charAt(0).toUpperCase() || "#").replace(/[^A-Z]/, "#");
      groups.set(key, [...(groups.get(key) ?? []), row]);
    }
    return [...groups.entries()];
  }, [sort, visibleCaptured]);
  const focusRecordAt = (next: number) => {
    const clamped = Math.min(Math.max(0, next), captured.length - 1);
    setActiveIndex(clamped);
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>(`[data-record-card-index="${clamped}"]`)?.focus();
    });
  };

  // Sticky-state detection for the controls bar. When the bar scrolls
  // up past the viewport top, an IntersectionObserver fires on the
  // sentinel just above it; we toggle a `data-stuck` attribute so the
  // CSS can animate into a compact pinned bar (backdrop, shadow,
  // slightly smaller paddings). Sentinel pattern is the standard way
  // to detect position:sticky engagement.
  const stickySentinelRef = useRef<HTMLDivElement | null>(null);
  const stickyBarRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    const bar = stickyBarRef.current;
    if (!sentinel || !bar) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        bar.dataset.stuck = entry.isIntersecting ? "false" : "true";
      },
      { rootMargin: "-1px 0px 0px 0px", threshold: [0, 1] },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="page-shell">
      {/* Single continuous card - bordeaux hero at top with the wave
          bottom, then the "All people" controls + list right below,
          all inside ONE page-card so the wave reads as the transition
          from hero to body (not a card with no bottom). The BTN grid
          that used to sit between them has been removed at user
          request: a case manager needs the search input and the list,
          not a hero-metric grid. */}
      <div className="records-editorial-surface" data-tour="people-list">
        <div
          className="page-hero records-ledger-masthead"
          style={{ position: "relative", overflow: "hidden" }}
        >
          <BordeauxOceanLayer surface="records" density={rosterDensity} energy={0.52} hue={0.42} />
          <HeroScrim strength="soft" />
          <div className="records-masthead-count" aria-hidden="true">
            {allCaptured.length.toLocaleString()}
          </div>
          <div style={{ position: "absolute", top: 10, right: 14, zIndex: 2 }}>
            <PageHelpHandle
              query="What is the People page for, and how should I use it to find someone?"
              topicId="the-njrc-program-flow"
            />
          </div>
          <div
            className="records-masthead-layout"
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 10,
              minWidth: 0,
              paddingRight: 42,
            }}
          >
            <div>
              <div className="page-hero-eyebrow">People</div>
              <h1 className="page-hero-title">Records index</h1>
              <div className="page-hero-caption">
                {allCaptured.length.toLocaleString()} captured people.{" "}
                <span
                  title={TRIAGE_EXPLAINER}
                  style={{ borderBottom: "1px dotted rgba(255,255,255,0.55)", cursor: "help" }}
                >
                  Priority score
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Body - section title + sticky controls + list. All inside
            the same page-card as the hero so the wave at the bottom of
            the hero leads visually into the content below. */}
        <div className="records-ledger-body">
          <h2 className="sr-only">All people</h2>
          <div
            className="records-desk-head ink-section"
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 14,
              paddingBottom: 12,
              borderBottom: "1px solid var(--color-line)",
            }}
          >
            <div className="meta" style={{ fontFamily: "var(--font-mono)", textTransform: "uppercase", letterSpacing: "0.16em" }}>
              People index · desk file A
            </div>
            <div className="meta tabular" style={{ fontFamily: "var(--font-mono)", color: stale ? "var(--color-amber-hi)" : "var(--color-text-dim)" }}>
              {lastUpdated ? `${stale ? "Stale" : "Updated"} ${lastUpdated.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "Loading index"}
            </div>
          </div>

          {/* Sentinel - invisible 1px element whose visibility we
              observe to detect when the sticky bar engages. Sits
              immediately above the bar so it leaves the viewport at
              the exact moment the bar would otherwise scroll off. */}
          <div ref={stickySentinelRef} style={{ height: 1, marginBottom: -1 }} aria-hidden />

          {/* Sticky controls bar - search + view toggle. position:sticky
              with top:0 pins to the viewport top once the user scrolls
              past. The data-stuck attribute (driven by the
              IntersectionObserver above) flips on/off CSS that adds a
              backdrop, shadow, and slightly tighter padding for the
              pinned state - so the transition reads as "settling into
              a toolbar" rather than "abruptly stuck". */}
          <div
            ref={stickyBarRef}
            data-stuck="false"
            className="records-sticky-controls"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              padding: "12px 0",
              marginInline: "calc(-1 * clamp(18px, 4vw, 64px))",
              paddingInline: "clamp(18px, 4vw, 64px)",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 14,
              marginBottom: 20,
              transition:
                "background 220ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 220ms cubic-bezier(0.16, 1, 0.3, 1), padding 220ms cubic-bezier(0.16, 1, 0.3, 1), backdrop-filter 220ms cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            {/* Search input - flexes to fill, anchors to left */}
            <div className="relative" style={{ flex: "1 1 280px", maxWidth: 480 }}>
              <input
                ref={searchInputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Name, AKA, DOB, or docket #"
              className="input records-command-input"
              autoFocus
              style={{ paddingLeft: 40, width: "100%" }}
              />
              <span
                className="pointer-events-none absolute top-1/2 -translate-y-1/2 text-[color:var(--color-text-dim)]"
                style={{ left: 14, fontSize: 16 }}
              >
                ⌕
              </span>
            </div>

            <label className="sr-only" htmlFor="records-sort">Sort records</label>
            <select
              id="records-sort"
              className="input"
              value={sort}
              onChange={(e) => setSort(e.target.value as RecordsSort)}
              style={{ flex: "0 1 220px", fontSize: 16, paddingBlock: 11 }}
            >
              <option value="urgency">Sort by urgency</option>
              <option value="balance">Sort by record count</option>
              <option value="nextDate">Sort by latest activity</option>
            </select>

            <div className="inline-flex flex-wrap gap-2" aria-label="Status filter">
              {(
                [
                  ["all", "All"],
                  ["open", "Open"],
                  ["outstanding", "Outstanding"],
                  ["cleared", "Cleared"],
                  ["processing", "In-progress"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStatusFilter(key)}
                  aria-current={statusFilter === key ? "true" : undefined}
                  className="tap-bump rounded-full px-3 py-1.5 text-sm font-semibold transition"
                  style={{
                    border: statusFilter === key ? "1px solid #8C1F2F" : "1px solid var(--color-line)",
                    background: statusFilter === key ? "#8C1F2F" : "var(--color-bg-card)",
                    color: statusFilter === key ? "#F2E9D6" : "var(--color-text-muted)",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <div
              aria-live="polite"
              className="meta tabular"
              style={{
                flex: "0 0 auto",
                color: "var(--color-text-muted)",
                fontFamily: "var(--font-mono)",
              }}
            >
              {captured.length.toLocaleString()} result{captured.length === 1 ? "" : "s"}
            </div>

            {/* View-mode toggle anchors to the right of the row. */}
            <div className="inline-flex rounded-lg border border-[color:var(--color-line)] p-1 text-sm" style={{ marginLeft: "auto" }}>
              {(
                [
                  ["ledger", "Ledger"],
                  ["table", "Table"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setViewPersisted(key as LedgerView)}
                  aria-current={view === key ? "true" : undefined}
                  className={`tap-bump rounded-md px-3 py-1.5 font-medium transition ${
                    view === key
                      ? "bg-[#8C1F2F] text-[#F2E9D6]"
                      : "text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div
            className="records-ledger-legend mb-4 px-3 py-2"
            style={{
              border: "1px solid var(--color-line)",
              background: "var(--color-bg-card)",
              color: "var(--color-text-muted)",
              fontSize: 16,
              lineHeight: 1.5,
              borderRadius: "var(--r-card)",
            }}
          >
            Legend: <strong>Cleared</strong> means captured and ready to review. <strong>Outstanding</strong> means higher follow-up urgency. <strong>Open</strong> means capture failed or was canceled. <strong>Priority</strong> is urgency from open items and court proximity, not a person risk score.
          </div>

        {loading || filtering ? (
          <div className="records-ledger" aria-busy="true" aria-label="Loading people">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="records-ledger-row records-ledger-row-skeleton">
                <div className="skeleton" style={{ width: 42, height: 22, borderRadius: "var(--r-control)" }} />
                <div className="skeleton" style={{ minWidth: 160, height: 24, borderRadius: "var(--r-control)" }} />
                <div className="skeleton records-ledger-leader" />
                <div className="skeleton" style={{ width: 86, height: 24, borderRadius: "var(--r-control)" }} />
                <div className="skeleton" style={{ width: 180, height: 34, borderRadius: "var(--r-control)" }} />
              </div>
            ))}
          </div>
        ) : loadError ? (
          <div className="card" role="alert" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div className="h3">Records index unavailable.</div>
              <p className="meta" style={{ marginTop: 6, color: "var(--color-text-muted)" }}>Source unavailable - retry. {loadError}</p>
            </div>
            <button type="button" className="btn btn-secondary" onClick={loadRecords}>Retry</button>
          </div>
        ) : captured.length === 0 && allCaptured.length === 0 ? (
          <div
            className="card text-center"
            style={{
              padding: "clamp(48px, 6vw, 96px) clamp(20px, 4vw, 48px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 18,
            }}
          >
            <div
              aria-hidden
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                background: "color-mix(in oklab, var(--color-bordeaux-hi) 14%, transparent)",
                color: "var(--color-bordeaux-hi)",
                fontFamily: "var(--font-serif)",
                fontSize: 36,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              +
            </div>
            <div className="display" style={{ fontSize: 28, lineHeight: 1.15, letterSpacing: "-0.01em" }}>
              No one's on the docket yet.
            </div>
            <p
              className="subline mx-auto"
              style={{ maxWidth: 480, color: "var(--color-text-muted)", fontSize: 14, lineHeight: 1.55 }}
            >
              Once you add someone via the intake form, the worker pulls their court, MVC, and corrections
              records and they'll show up here for review.
            </p>
            <Link
              to="/lookup"
              className="btn btn-primary"
              style={{ marginTop: 4, paddingInline: 22, paddingBlock: 12, fontSize: 13 }}
            >
              Add the first person →
            </Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("njrc:open-assistant", {
                detail: { query: "How do I add my first person to the docket?" },
              }))}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--color-text-dim)",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                cursor: "pointer",
                marginTop: 4,
              }}
            >
              Or ask Jack how this works ›
            </button>
          </div>
        ) : captured.length === 0 ? (
          /* Records exist but the search filtered them all out. Show a
             distinct empty state so the user doesn't think their data
             vanished. */
          <div className="card text-center py-12">
            <div className="h2-tight text-[color:var(--color-text-dim)]">
              No matches for &ldquo;{query.trim()}&rdquo;.
            </div>
            <p className="subline mx-auto mt-2">
              Try a different name or DOB, {" "}
              <button
                type="button"
                onClick={() => setQuery("")}
                className="underline decoration-dotted underline-offset-4"
                style={{ background: "transparent", border: "none", color: "inherit", padding: 0, cursor: "pointer" }}
              >
                clear search
              </button>{" "}
              to see all {allCaptured.length} captured records.
            </p>
          </div>
        ) : view === "table" ? (
          /* Compact tabular view - five columns sized intentionally so name
             gets the room it needs and scores get a tight tabular column. */
          <>
          <div className="card overflow-x-auto p-0">
            <table className="w-full min-w-[680px] border-collapse">
              <thead>
                <tr className="border-b border-[color:var(--color-line)] text-left">
                  {[
                    { label: "Name", align: "left" as const },
                    { label: "Status", align: "left" as const },
                    { label: "DOB", align: "left" as const },
                    { label: "Records", align: "right" as const },
                    { label: "Priority", align: "right" as const },
                    { label: "Added", align: "left" as const },
                  ].map((h) => (
                    <th
                      key={h.label}
                      className={`px-4 py-4 font-semibold uppercase tracking-wider text-[color:var(--color-text-dim)] whitespace-nowrap ${h.align === "right" ? "text-right" : "text-left"}`}
                      style={{ fontSize: 14, letterSpacing: "0.12em" }}
                    >
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleCaptured.map((j) => {
                  const token = statusToken(j);
                  const inProgress = j.status === "queued" || j.status === "running" || j.status === "waiting_captcha";
                  return (
                    <tr
                      key={j.id}
                      className={
                        inProgress
                          ? "group border-b border-[color:var(--color-line)] last:border-b-0"
                          : "group cursor-pointer border-b border-[color:var(--color-line)] last:border-b-0 hover:bg-[color:var(--color-bg-card-hi)] transition-colors"
                      }
                    >
                      <td className="px-4 py-5">
                        {inProgress ? (
                          <div
                            className="block"
                            title="Still capturing: page will be available once finished."
                            style={{ cursor: "wait", opacity: 0.78 }}
                          >
                            <div className="flex items-baseline gap-2.5">
                              <div
                                className="display text-[color:var(--color-text)]"
                                style={{
                                  fontSize: "clamp(18px, 1.5vw, 22px)",
                                  lineHeight: 1.1,
                                  fontVariationSettings: '"opsz" 36, "SOFT" 50',
                                }}
                            >
                              {j.last_name}, {j.first_name}
                            </div>
                          </div>
                        </div>
                        ) : (
                          <Link to={`/records/${j.id}`} className="block">
                            <div className="flex items-baseline gap-2.5">
                              <div
                                className="display group-hover:underline text-[color:var(--color-text)]"
                                style={{
                                  fontSize: "clamp(18px, 1.5vw, 22px)",
                                  lineHeight: 1.1,
                                  fontVariationSettings: '"opsz" 36, "SOFT" 50',
                                }}
                              >
                                {j.last_name}, {j.first_name}
                              </div>
                            </div>
                          </Link>
                        )}
                      </td>
                      <td className="px-4 py-5">
                        {token ? (
                          <StatusChip state={token.chipState} label={token.label} title={token.detail} />
                        ) : (
                          <span className="meta text-[color:var(--color-text-dim)]">Not available</span>
                        )}
                        {(j.status === "error" || j.status === "canceled") && (
                          <div className="mt-2 flex items-center gap-2 text-[12px]" style={{ color: "var(--color-rose-hi)" }}>
                            <span aria-hidden="true">!</span>
                            <span>Source unavailable - retry</span>
                            <button
                              type="button"
                              onClick={() => retryCapture(j.id)}
                              disabled={retryingId === j.id}
                              className="rounded px-2 py-1 font-semibold"
                              style={{
                                border: "1px solid rgba(255,120,120,0.35)",
                                background: "rgba(255,120,120,0.10)",
                                color: "var(--color-rose-hi)",
                              }}
                            >
                              {retryingId === j.id ? "Retrying..." : "Retry"}
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-5 mono whitespace-nowrap" style={{ color: "var(--color-text)", fontSize: "clamp(14px, 1vw, 16px)" }}>
                        {j.date_of_birth}
                      </td>
                      <td className="px-4 py-5 text-right">
                        <span
                          className="display tabular text-[color:var(--color-text)]"
                          style={{ fontSize: "clamp(22px, 2vw, 28px)", lineHeight: 1, fontWeight: 700 }}
                        >
                          {j.record_count_captured ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-5 text-right">
                        <span
                          className="display tabular text-[color:var(--color-text)]"
                          style={{ fontSize: "clamp(22px, 2vw, 28px)", lineHeight: 1, fontWeight: 700 }}
                        >
                          {j.havoc_score ?? "NA"}
                        </span>
                      </td>
                      <td className="px-4 py-5 whitespace-nowrap" style={{ color: "var(--color-text-muted)", fontSize: "clamp(14px, 1vw, 15px)" }}>
                        {formatDate(j.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <LedgerPager
            page={safePage}
            pageCount={pageCount}
            start={pageStart}
            visibleCount={visibleCaptured.length}
            total={captured.length}
            onPage={setPage}
          />
          </>
        ) : (
          <div
            className="records-ledger"
            role="list"
            aria-label="People records"
            onKeyDown={(e) => {
              if (captured.length === 0) return;
              if (e.key === "ArrowDown") { e.preventDefault(); focusRecordAt(activeIndex + 1); }
              if (e.key === "ArrowUp") { e.preventDefault(); focusRecordAt(activeIndex - 1); }
              if (e.key === "Home") { e.preventDefault(); focusRecordAt(0); }
              if (e.key === "End") { e.preventDefault(); focusRecordAt(captured.length - 1); }
            }}
          >
            <div className="records-ledger-column-head" role="row" aria-hidden="true">
              <span />
              <span>Name</span>
              <span />
              <span>Records</span>
              <span>Status</span>
              <span>Priority</span>
              <span>Last checked</span>
            </div>
            {groupedLedgerRows.map(([group, rows]) => (
              <section key={group} className="records-ledger-group" aria-label={group}>
                <div className="records-ledger-head">{group}</div>
                <ol className="records-ledger-list">
                  {rows.map((j, localIdx) => {
                    const visibleIdx = visibleCaptured.findIndex((row) => row.id === j.id);
                    const globalIdx = visibleIdx >= 0 ? pageStart + visibleIdx : pageStart + localIdx;
                    return (
                      <LedgerRow
                        key={j.id}
                        job={j}
                        index={globalIdx >= pageStart ? globalIdx : pageStart + localIdx}
                        active={globalIdx === activeIndex}
                        retrying={retryingId === j.id}
                        onFocus={() => setActiveIndex(globalIdx)}
                        onRetry={() => void retryCapture(j.id)}
                      />
                    );
                  })}
                </ol>
              </section>
            ))}
          </div>
        )}
        {!loading && !loadError && view !== "table" && captured.length > 0 && (
          <LedgerPager
            page={safePage}
            pageCount={pageCount}
            start={pageStart}
            visibleCount={visibleCaptured.length}
            total={captured.length}
            onPage={setPage}
          />
        )}
        </div>
      </div>
    </div>
  );
}

function LedgerRow({
  job,
  index,
  active,
  retrying,
  onFocus,
  onRetry,
}: {
  job: JobRow;
  index: number;
  active: boolean;
  retrying: boolean;
  onFocus: () => void;
  onRetry: () => void;
}) {
  const token = statusToken(job);
  const inProgress = isInProgress(job);
  const asOf = job.completed_at ?? job.started_at ?? job.created_at;
  return (
    <li className="records-ledger-item" role="row">
      <Link
        to={`/records/${job.id}`}
        id={`record-ledger-${job.id}`}
        data-record-card-index={index}
        aria-current={active ? "true" : undefined}
        tabIndex={active ? 0 : -1}
        onFocus={onFocus}
        className="records-ledger-row"
        data-ledger-ordinal={String(index + 1).padStart(2, "0")}
        title={inProgress ? "Still capturing: click to see per-source scrape status." : undefined}
      >
        <span className="records-ledger-index tabular">{String(index + 1).padStart(3, "0")}</span>
        <span className="records-ledger-name">
          {job.last_name}, {job.first_name}
        </span>
        <span className="records-ledger-leader" aria-hidden="true" />
        <span className="records-ledger-count tabular">
          {job.record_count_captured ?? 0} rec{(job.record_count_captured ?? 0) === 1 ? "" : "s"}
        </span>
        <span className="records-ledger-status">
          <StatusChip state={token.chipState} label={token.label} title={token.detail} />
        </span>
        <span className="records-ledger-priority tabular" title={TRIAGE_EXPLAINER}>
          Priority {job.havoc_score ?? "NA"}
        </span>
        <span className="records-ledger-rail">
          <span>MCCS plus source checks</span>
          <span className="tabular">as of {formatDate(asOf)} {formatTime(asOf)}</span>
        </span>
        {(job.status === "error" || job.status === "canceled") && (
          <span className="records-ledger-error" onClick={(e) => e.preventDefault()}>
            <span>Source unavailable.</span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRetry();
              }}
              disabled={retrying}
            >
              {retrying ? "Retrying..." : "Retry"}
            </button>
          </span>
        )}
      </Link>
    </li>
  );
}

function LedgerPager({
  page,
  pageCount,
  start,
  visibleCount,
  total,
  onPage,
}: {
  page: number;
  pageCount: number;
  start: number;
  visibleCount: number;
  total: number;
  onPage: (page: number) => void;
}) {
  if (total <= LEDGER_PAGE_SIZE) return null;
  const end = start + visibleCount;
  return (
    <div className="records-ledger-pager" aria-label="Ledger pagination">
      <div className="meta tabular">
        Showing {start + 1}-{end} of {total.toLocaleString()}
      </div>
      <div className="inline-flex gap-2">
        <button
          type="button"
          className="btn btn-secondary"
          disabled={page <= 0}
          onClick={() => onPage(Math.max(0, page - 1))}
        >
          Previous
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={page >= pageCount - 1}
          onClick={() => onPage(Math.min(pageCount - 1, page + 1))}
        >
          Next
        </button>
      </div>
    </div>
  );
}
